import { bech32 } from 'bech32'
import { defineNuxtPlugin } from '#app'
import { Network } from '@shared/types'
import { defineRule } from 'vee-validate'
import { injToken } from '@shared/data/token'
import { toBigNumber } from '@injectivelabs/utils'
import { NUMBER_REGEX } from '@shared/utils/constant'
import { getNetworkFromAddress } from '@shared/utils/network'
import { getEthereumAddress } from '@injectivelabs/sdk-ts/utils'
import { INJ_REQUIRED_FOR_GAS } from '@/app/utils/constants'
import { isValidEvmAddress, isValidSolanaAddress } from '@/utils/layerzero'
import {
  isValidInjDomainName,
  isValidInjBonfidaDomainName
} from '@/app/utils/nameService'

const ethAddressRegex = /^0x[0-9a-fA-F]{40}$/i

const injAddressRegex = /^inj1[ac-hj-np-z02-9]{38}$/i

const formatInvalidAddressMsg = (network: string) =>
  `This field is not a valid ${network} address`

export const errorMessages = {
  email: () => 'Invalid email',
  emailRequired: () => 'Email is required',
  incorrectFormat: () => 'Incorrect format',
  maxBalance: () => 'Insufficient balance',
  maxBalanceWithGasBuffer: () =>
    `Insufficient balance. Gas fees are ${INJ_REQUIRED_FOR_GAS} INJ`,
  feeWarning: () =>
    `Insufficient balance. Gas fees are ${INJ_REQUIRED_FOR_GAS} INJ`,
  maxUsdAmount: (amount: string) => `Amount must be under $${amount}`,
  ethAddress: () => `This field is not a valid Ethereum address`,
  injName: () => 'This field is not a valid Injective name',
  injBonfidaName: () => 'This field is not a valid Injective name',
  [Network.Kava]: () => formatInvalidAddressMsg('Kava'),
  [Network.Evmos]: () => formatInvalidAddressMsg('Evmos'),
  [Network.Noble]: () => formatInvalidAddressMsg('Noble'),
  [Network.Canto]: () => formatInvalidAddressMsg('Canto'),
  [Network.Axelar]: () => formatInvalidAddressMsg('Axelar'),
  [Network.Stride]: () => formatInvalidAddressMsg('Stride'),
  [Network.Kujira]: () => formatInvalidAddressMsg('Kujira'),
  [Network.Osmosis]: () => formatInvalidAddressMsg('Osmosis'),
  [Network.CosmosHub]: () => formatInvalidAddressMsg('Cosmos'),
  [Network.Ethereum]: () => formatInvalidAddressMsg('Ethereum'),
  [Network.Crescent]: () => formatInvalidAddressMsg('Crescent'),
  [Network.Celestia]: () => formatInvalidAddressMsg('Celestia'),
  [Network.Andromeda]: () => formatInvalidAddressMsg('Andromeda'),
  [Network.Injective]: () => formatInvalidAddressMsg('Injective'),
  [Network.Sommelier]: () => formatInvalidAddressMsg('Sommelier'),
  [Network.Oraichain]: () => formatInvalidAddressMsg('Oraichain'),
  [Network.Secret]: () => formatInvalidAddressMsg('Secret Network'),
  [Network.Persistence]: () => formatInvalidAddressMsg('Persistence'),
  [Network.EvmosTestnet]: () => formatInvalidAddressMsg('Evmos'),
  [Network.XionTestnet]: () => formatInvalidAddressMsg('Xion'),
  [Network.Xion]: () => formatInvalidAddressMsg('Xion')
} as Record<string, any>

const formatFieldName = ({
  field,
  label
}: {
  field: string
  label?: string
}) => {
  if (label) {
    return label
  }

  if (field.includes('-')) {
    return field.replaceAll('-', ' ')
  }

  return field.replace(/[^a-z]+/gi, '')
}

export const defineGlobalRules = () => {
  defineRule(
    'required',
    (
      value: string | number,
      [label]: [string | undefined],
      { field }: { field: string }
    ) => {
      if (!value || !value.toString().length || Number(value) === 0) {
        if (field.toLowerCase().includes('amount')) {
          return 'amount is required'
        }

        if (field === 'addressOrPrivateKey') {
          return 'Address or private key is required'
        }

        if (field === 'layerzero-destination-address') {
          return 'Destination address required'
        }

        return `${formatFieldName({ field, label })} is required`
      }

      return true
    }
  )

  defineRule('injAddress', (value: string) => {
    const errorMessage = 'Invalid INJ address'

    if (value?.startsWith('0x') || !value?.startsWith(injToken.denom)) {
      return errorMessage
    }

    try {
      if (getEthereumAddress(value)) {
        return true
      } else {
        return errorMessage
      }
    } catch {
      return errorMessage
    }
  })

  defineRule('privateKey', (value: string) => {
    const errorMessage = 'Invalid private key'

    if (!value) {
      return errorMessage
    }

    // Remove '0x' prefix if present
    const cleanValue = value.startsWith('0x') ? value.slice(2) : value

    // Check if it's a valid hex string of 64 characters (32 bytes)
    if (!/^[0-9a-fA-F]{64}$/.test(cleanValue)) {
      return errorMessage
    }

    return true
  })

  defineRule('injName', (value: string) => {
    if (!value) {
      return true
    }

    if (!isValidInjDomainName(value)) {
      return errorMessages.injName(value)
    }

    return true
  })

  defineRule('injBonfidaName', (value: string) => {
    if (!value) {
      return true
    }

    if (!isValidInjBonfidaDomainName(value)) {
      return errorMessages.injBonfidaName(value)
    }

    return true
  })

  defineRule('addressByNetwork', (value: string, [network]: Network[]) => {
    if (!network) {
      return true
    }

    if (network === Network.Ethereum) {
      if (!value.startsWith('0x') || value.length !== 42) {
        const errorFn = errorMessages[network]

        return errorFn ? errorFn() : true
      }

      return true
    }

    // cosmos address
    try {
      const networkFromAddress = getNetworkFromAddress(value)

      if (networkFromAddress !== network) {
        const errorFn = errorMessages[network]

        return errorFn ? errorFn() : true
      }

      bech32.decode(value)

      return true
    } catch {
      const errorFn = errorMessages[network]
      if (errorFn) {
        return errorFn()
      }
    }
  })

  defineRule('ethOrPeggyAddress', (value: string) => {
    if (!value) {
      return true
    }

    const formattedAddress = value.replace('peggy', '')

    if (!formattedAddress.startsWith('0x') || formattedAddress.length !== 42) {
      return errorMessages.ethAddress()
    }

    return true
  })

  defineRule('solanaAddress', (value: string) => {
    if (!value) {
      return true
    }

    if (!isValidSolanaAddress(value)) {
      return 'This field is not a valid Solana address'
    }

    return true
  })

  defineRule('evmAddress', (value: string) => {
    if (!value) {
      return true
    }

    return isValidEvmAddress(value) || errorMessages.ethAddress()
  })

  defineRule('txHash', (value: string) => {
    if (!value) {
      return true
    }

    if (!/^0x[0-9a-fA-F]{64}$/.test(value)) {
      return 'Enter a valid 0x-prefixed 32-byte transaction hash'
    }

    return true
  })

  defineRule('isNumber', (value: string) => {
    if (value && !NUMBER_REGEX.test(value)) {
      return errorMessages.incorrectFormat()
    }

    return true
  })

  defineRule(
    'min',
    (
      value: string,
      [min, label]: [number | string, string | undefined],
      { field }: { field: string }
    ) => {
      const fieldName = formatFieldName({ field, label })
      const valueInNumber = Number.isNaN(value) && value ? value.length : value
      const valueInBigNumber = toBigNumber(valueInNumber)

      if (valueInBigNumber.lte(min)) {
        return `${label || fieldName} needs to be greater than ${min}`
      }

      return true
    }
  )

  defineRule(
    'max',
    (
      value: string,
      [max, label]: [number | string, string | undefined],
      { field }: { field: string }
    ) => {
      const fieldName = formatFieldName({ field, label })
      const valueInNumber = Number.isNaN(value) && value ? value.length : value
      const valueInBigNumber = toBigNumber(valueInNumber)

      if (valueInBigNumber.gt(max)) {
        return `${label || fieldName} cannot be greater than ${max}`
      }

      return true
    }
  )

  defineRule(
    'minInclusive',
    (
      value: string,
      [min, label]: [number | string, string | undefined],
      { field }: { field: string }
    ) => {
      if (!value) {
        return true
      }

      const fieldName = formatFieldName({ field, label })
      const valueInNumber = Number.isNaN(value) && value ? value.length : value
      const valueInBigNumber = toBigNumber(valueInNumber)

      if (valueInBigNumber.lt(min)) {
        return `${label || fieldName} needs to be greater than or equal to ${min}`
      }

      return true
    }
  )

  defineRule(
    'maxAllowedUsdAmount',
    (value: string, [max, usdPrice]: [string, string | undefined]) => {
      const usdAmountInBigNumber = toBigNumber(value).multipliedBy(
        usdPrice || 0
      )

      if (usdAmountInBigNumber.gt(max)) {
        return errorMessages.maxUsdAmount(max)
      }

      return true
    }
  )

  defineRule('maxBalance', (value: string, [max]: [string]) => {
    if (!value) {
      return true
    }

    const valueIsGreaterThanMax = toBigNumber(value).gt(max)

    if (valueIsGreaterThanMax) {
      return errorMessages.maxBalance()
    }

    return true
  })

  defineRule('email', (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(value)) {
      return errorMessages.email()
    }

    return true
  })

  defineRule('emailRequired', (value: string) => {
    if (!value) {
      return errorMessages.emailRequired()
    }

    return true
  })

  defineRule('feeWarning', (value: string, [balance]: [string]) => {
    const isInsufficientBalance = toBigNumber(balance)
      .minus(value)
      .isLessThan(INJ_REQUIRED_FOR_GAS)

    if (isInsufficientBalance) {
      return errorMessages.feeWarning()
    }

    return true
  })

  defineRule('injOrEthAddress', (value: string) => {
    if (!value) {
      return true
    }

    const isEthereumAddress = ethAddressRegex.test(value)
    const isInjectiveAddress = injAddressRegex.test(value)

    if (isEthereumAddress || isInjectiveAddress) {
      return true
    }

    return errorMessages.ethAddress()
  })
}

export default defineNuxtPlugin(() => {
  defineGlobalRules()
})
